import React, { useLayoutEffect, useRef, useState } from 'react'

// The rail is the inline-start strip of the panel, so its centre is on the right in RTL.
const railCentre = (direction, panelWidth, railWidth) => (direction === 'rtl' ? panelWidth - railWidth / 2 : railWidth / 2)

// One segmented control in both sidebar states. While the rail is collapsed, CSS slides
// the group so the active language sits centred on the rail and shrinks the ring around
// just that item (see .sb-lang__group in styles/SideBar.css); the inactive items fade like
// nav labels. The ring is two end caps plus a middle segment so it can change width with
// transforms alone. The CSS variables that drive it are measured here from the resting
// layout, so the active language is drawn once and never jumps.

// Ring geometry in SideBar.css: the caps are RING_CAP wide (= the ring's corner radius),
// the middle segment spans the rest.
const RING_CAP = 23
const LanguageSelector = ({ langs, selectedLang, onLanguageChange }) => {
    const groupRef = useRef(null)
    const itemRefs = useRef({})
    const [vars, setVars] = useState({})

    useLayoutEffect(() => {
        const group = groupRef.current
        const measure = () => {
            const active = itemRefs.current[selectedLang]
            if (!group || !active) return
            const groupStyle = getComputedStyle(group)
            const activeStyle = getComputedStyle(active)
            const rail = parseFloat(groupStyle.getPropertyValue('--sb-rail')) || 80
            const expanded = parseFloat(groupStyle.getPropertyValue('--sb-expanded')) || 280

            // offsetLeft ignores transforms, so this is the resting layout even mid-sweep.
            // group.offsetLeft is relative to .sb-lang, which spans the panel; active.offsetLeft
            // is relative to the group's padding box, hence the border (clientLeft) term.
            const border = group.clientLeft
            const activeLeft = border + active.offsetLeft
            const activeRight = activeLeft + active.offsetWidth
            const shift = railCentre(groupStyle.direction, expanded, rail) - (group.offsetLeft + (activeLeft + activeRight) / 2)

            // The ring keeps the group's padding and the item's margin around the active
            // item, i.e. the same pill the rail showed before.
            const hugLeft = border + parseFloat(groupStyle.paddingLeft) + parseFloat(activeStyle.marginLeft)
            const hugRight = border + parseFloat(groupStyle.paddingRight) + parseFloat(activeStyle.marginRight)
            const clipLeft = Math.max(0, activeLeft - hugLeft)
            const clipRight = Math.max(0, group.offsetWidth - activeRight - hugRight)
            const middleOpen = group.offsetWidth - 2 * RING_CAP
            const middleCollapsed = middleOpen - clipLeft - clipRight
            const middleScale = middleOpen > 0 ? Math.max(0, middleCollapsed / middleOpen) : 1

            setVars({
                '--sb-lang-shift': `${shift}px`,
                '--sb-lang-clip-l': `${clipLeft}px`,
                '--sb-lang-clip-r': `${clipRight}px`,
                '--sb-lang-mid-scale': middleScale,
            })
        }

        measure()

        // Web fonts or a locale change can resize the group after the first measure.
        if (!group || typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(measure)
        observer.observe(group)
        return () => observer.disconnect()
    }, [selectedLang, langs])

    return (
        <div className="sb-lang mt-1 xl3:mt-6">
            <div
                ref={groupRef}
                className="sb-lang__group lang-changer-group border p-2 pointer font-bold text-lg border-mudPurple text-[#9392AD] rounded-full flex flex-row w-fit"
                style={vars}
            >
                {/* Middle segment of the ring; the end caps are the group's ::before/::after. */}
                <span className="sb-lang__ring" aria-hidden="true" />
                {langs?.map((lang) => {
                    const active = lang.code === selectedLang
                    return (
                        <button
                            type="button"
                            ref={(el) => {
                                itemRefs.current[lang.code] = el
                            }}
                            className={`lang-item mx-2 ${active ? 'text-white' : 'sb-reveal'}`}
                            key={lang.code}
                            disabled={active}
                            onClick={(e) => {
                                e.preventDefault()
                                onLanguageChange(lang.code)
                            }}
                        >
                            {lang.name}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default LanguageSelector
